import { ref } from 'virtual-vue';
import platform, { PlatformEnum } from '@tencent/wphk-platform-adapter';
import { clamp } from '../../shared/number';

/** 触摸采样点，用于计算滑动速度 */
interface TouchPoint {
  y: number;
  time: number;
}

interface UseColumnTouchOptions {
  /** 获取当前列的选项总数 */
  getOptionCount: () => number;
  /** 获取单个选项的实际像素高度（通过 getBoundingClientRect 测量） */
  getItemHeight: () => number;
  /** 获取可见选项个数（决定滚动区域高度和居中偏移计算） */
  visibleOptionNum: () => number;
  /** 获取惯性滚动的基础时长配置 */
  swipeDuration: () => number;
  /** 滚动到目标索引后的回调，通知父组件选中项变化 */
  onScrollTo: (_index: number) => void;
  /** 是否处于禁用状态 */
  isDisabled: () => boolean;
}

/**
 * 从 touch 事件中提取 clientY。
 * H5: event.touches[0].clientY
 * LiteApp: event.detail.clientY（原生组件的 touch 事件结构不同）
 */
function getClientY(event: any): number {
  if (platform.platformType === PlatformEnum.LiteApp) {
    return event?.detail?.clientY ?? 0;
  }
  return event?.touches?.[0]?.clientY ?? 0;
}

const raf = typeof requestAnimationFrame === 'function'
  ? requestAnimationFrame
  : (cb: (_time: number) => void) => setTimeout(() => cb(Date.now()), 16) as unknown as number;

const cancelRaf = typeof cancelAnimationFrame === 'function'
  ? cancelAnimationFrame
  : (id: number) => clearTimeout(id);

/** 滑动距离低于此值（px）视为点击而非滑动 */
const MIN_SWIPE_DISTANCE = 5;
/** 松手前手指静止超过此时长（ms）则不触发惯性滚动 */
const STOP_TIME_THRESHOLD = 100;
/** 从采样点中取最近此时长（ms）窗口内的点计算速度 */
const VELOCITY_SAMPLE_WINDOW = 300;
/** 最多保留的触摸采样点数量，超出后移除最早的点 */
const MAX_SAMPLE_POINTS = 40;
/** 非惯性场景下，自动对齐到最近选项的 CSS transition 时长（ms） */
const TRANSITION_DURATION = 300;
/** 惯性动画的最小持续时间（ms） */
const MIN_INERTIA_DURATION = 500;
/** 惯性摩擦系数，惯性距离 = speed / INERTIA_FRICTION × direction。值越大阻力越大 */
const INERTIA_FRICTION = 0.003;

/**
 * 四次方缓出函数（quartEaseOut），用于惯性滚动的逐帧插值。
 * 曲线特征：开始快、结束慢，模拟手指松开后的减速效果。
 * @param t 当前时间（已过去的 ms）
 * @param b 起始值
 * @param c 变化量（终值 - 起始值）
 * @param d 总时长（ms）
 */
function quartEaseOut(t: number, b: number, c: number, d: number): number {
  const tempT = t / d - 1;
  return -c * (tempT * tempT * tempT * tempT - 1) + b;
}

/**
 * PickerColumn 核心触摸交互 composable。
 *
 * 职责：处理 touchstart/move/end 事件，实现滑动选择、惯性滚动、自动对齐。
 *
 * 偏移量模型：
 *   baseOffset = itemHeight × (visibleOptionNum - 1) / 2
 *   选项 i 居中时 offset = baseOffset - i × itemHeight
 *   offset 越大 → 列表越往下 → 选中索引越小
 */
export function useColumnTouch(options: UseColumnTouchOptions) {
  const {
    getOptionCount,
    getItemHeight,
    visibleOptionNum,
    swipeDuration,
    onScrollTo,
    isDisabled,
  } = options;

  /** 当前 translateY 偏移量（px），驱动列表位置 */
  const offset = ref(0);
  /** CSS transition 时长（ms），为 0 时无过渡（惯性动画自行驱动） */
  const duration = ref(0);
  /** 是否正在触摸拖动中 */
  const moving = ref(false);

  let startY = 0;
  let startTime = 0;
  let startOffset = 0;
  let lastMoveTime = 0;
  /** 标记位，用于中断正在进行的 rAF 惯性动画 */
  let stopInertiaMove = false;
  let animationFrameId: number | null = null;
  /** 触摸轨迹采样点，用于在 touchend 时计算滑动速度 */
  const points: TouchPoint[] = [];

  /** 计算使第一个选项居中时的偏移量（即 index=0 的居中位置） */
  const getBaseOffset = (): number => {
    const h = getItemHeight();
    return (h * (visibleOptionNum() - 1)) / 2;
  };

  /** 最小偏移量：最后一个选项居中时的 offset */
  const getMinOffset = (): number => {
    const count = getOptionCount();
    const h = getItemHeight();
    return -(count - 1) * h + getBaseOffset();
  };

  /** 最大偏移量：第一个选项居中时的 offset（等于 baseOffset） */
  const getMaxOffset = (): number => getBaseOffset();

  /** 根据当前偏移量反算出最近的选项索引，并 clamp 到合法范围 */
  const getIndexByOffset = (currentOffset: number): number => {
    const h = getItemHeight();
    if (h <= 0) return 0;
    const base = getBaseOffset();
    const rawIndex = Math.round((base - currentOffset) / h);
    const count = getOptionCount();
    return clamp(rawIndex, 0, count - 1);
  };

  /** 取消正在进行的 rAF 惯性动画 */
  const cancelAnimation = () => {
    if (animationFrameId !== null) {
      cancelRaf(animationFrameId);
      animationFrameId = null;
    }
    stopInertiaMove = true;
  };

  /** 滚动到指定索引位置，immediate=true 时无过渡动画 */
  const scrollToIndex = (index: number, immediate = false) => {
    cancelAnimation();
    const h = getItemHeight();
    const base = getBaseOffset();
    const targetOffset = base - index * h;
    duration.value = immediate ? 0 : TRANSITION_DURATION;
    offset.value = targetOffset;
  };

  /**
   * 限制偏移量范围，超出边界时使用橡皮筋阻尼效果。
   * 边界内：1:1 跟手；超出后：位移按对数衰减，拉得越远阻力越大。
   */
  const clampOffset = (val: number): number => {
    const min = getMinOffset();
    const max = getMaxOffset();
    const h = getItemHeight();
    const boundary = h;

    if (val > max) {
      const over = val - max;
      return max + boundary * Math.log10(1 + over / boundary);
    }
    if (val < min) {
      const over = min - val;
      return min - boundary * Math.log10(1 + over / boundary);
    }
    return val;
  };

  /**
   * 执行惯性滚动动画。
   * 使用 rAF 逐帧驱动 + quartEaseOut 缓动插值，而非 CSS transition，
   * 以获得更精细的动画控制和随时中断的能力。
   * 动画结束后自动对齐到最近选项并触发回调。
   */
  const scrollWithMomentum = (
    fromOffset: number,
    toOffset: number,
    animDuration: number,
  ) => {
    stopInertiaMove = false;
    duration.value = 0;
    let startTimestamp: number | null = null;

    const step = (timestamp: number) => {
      if (stopInertiaMove) return;
      if (startTimestamp === null) startTimestamp = timestamp;

      const progress = timestamp - startTimestamp;
      const cappedProgress = Math.min(progress, animDuration);
      const newOffset = quartEaseOut(
        cappedProgress,
        fromOffset,
        toOffset - fromOffset,
        animDuration,
      );
      offset.value = newOffset;

      if (progress < animDuration) {
        animationFrameId = raf(step);
      } else {
        const index = getIndexByOffset(newOffset);
        scrollToIndex(index);
        onScrollTo(index);
      }
    };

    animationFrameId = raf(step);
  };

  let moveCount = 0;

  /** 停止当前动画，记录起始状态，初始化采样数组 */
  const onTouchStart = (event: any) => {
    if (isDisabled()) return;
    cancelAnimation();

    const y = getClientY(event);
    console.log('[Touch] onTouchStart', { y, raw: JSON.stringify(event?.detail), hasTouches: !!event?.touches });
    startY = y;
    startTime = Date.now();
    startOffset = offset.value;
    lastMoveTime = startTime;
    duration.value = 0;
    moving.value = true;
    points.length = 0;
    points.push({ y, time: startTime });
    moveCount = 0;
  };

  /** 实时跟踪手指位置更新偏移量，同时持续采样用于速度计算 */
  const onTouchMove = (event: any) => {
    if (isDisabled() || !moving.value) return;
    if (typeof event.preventDefault === 'function') {
      event.preventDefault();
      event.stopPropagation();
    }

    const y = getClientY(event);
    const deltaY = y - startY;
    offset.value = clampOffset(startOffset + deltaY);
    moveCount += 1;
    if (moveCount <= 3 || moveCount % 10 === 0) {
      console.log('[Touch] onTouchMove', { moveCount, y, deltaY: Math.round(deltaY), offset: Math.round(offset.value) });
    }

    const now = Date.now();
    lastMoveTime = now;
    points.push({ y, time: now });
    if (points.length > MAX_SAMPLE_POINTS) {
      points.shift();
    }
  };

  /**
   * 触摸结束后的核心判定逻辑，依次判断三种情况：
   * 1. 滑动距离 < 5px → 视为点击，对齐最近选项
   * 2. 手指已停住超过 100ms → 无惯性，直接对齐
   * 3. 其他 → 从采样窗口计算速度，速度 > 0.1px/ms 时触发惯性滚动
   */
  const onTouchEnd = (_event?: any) => {
    if (isDisabled() || !moving.value) return;
    moving.value = false;

    const endTime = Date.now();
    const totalDistance = Math.abs(offset.value - startOffset);
    const totalTime = endTime - startTime;

    console.log('[Touch] onTouchEnd', { moveCount, totalDistance: Math.round(totalDistance), totalTime, offset: Math.round(offset.value) });

    // 滑动距离极小 → 视为点击，直接对齐最近选项
    if (totalDistance < MIN_SWIPE_DISTANCE) {
      console.log('[Touch] → tap (distance too small)');
      const index = getIndexByOffset(offset.value);
      scrollToIndex(index);
      onScrollTo(index);
      return;
    }

    const moveDistance = offset.value - startOffset;
    const timeSinceLastMove = endTime - lastMoveTime;

    // 手指已经停住超过阈值 → 无惯性，直接对齐
    if (timeSinceLastMove > STOP_TIME_THRESHOLD) {
      console.log('[Touch] → snap (finger stopped)', { timeSinceLastMove });
      const index = getIndexByOffset(offset.value);
      scrollToIndex(index);
      onScrollTo(index);
      return;
    }

    // 从采样点中取最近一段时间窗口内的点计算速度
    const recentPoints = points.filter(
      p => endTime - p.time <= VELOCITY_SAMPLE_WINDOW,
    );

    if (recentPoints.length >= 2) {
      const first = recentPoints[0];
      const last = recentPoints[recentPoints.length - 1];
      const timeDelta = last.time - first.time;

      if (timeDelta > 0) {
        const velocity = (last.y - first.y) / timeDelta;
        const speed = Math.abs(velocity);

        console.log('[Touch] velocity', { speed: speed.toFixed(3), recentPointsCount: recentPoints.length, timeDelta });

        // 速度和距离都足够才触发惯性（距离至少超过半个选项高度）
        const h = getItemHeight();
        if (speed > 0.3 && totalDistance > h * 1.5) {
          const direction = moveDistance < 0 ? -1 : 1;
          const inertiaDistance = (speed / INERTIA_FRICTION) * direction;
          const targetRaw = offset.value + inertiaDistance;
          const clamped = Math.max(
            getMinOffset(),
            Math.min(targetRaw, getMaxOffset()),
          );
          const targetIndex = getIndexByOffset(clamped);
          const finalOffset = getBaseOffset() - targetIndex * h;

          const distance = Math.abs(finalOffset - offset.value);
          const baseDuration = Math.max(swipeDuration(), MIN_INERTIA_DURATION);
          const dynamicDuration = Math.min(
            baseDuration + distance * 1.5,
            2000,
          );

          console.log('[Touch] → inertia', { targetIndex, finalOffset: Math.round(finalOffset), distance: Math.round(distance), dynamicDuration: Math.round(dynamicDuration) });
          scrollWithMomentum(offset.value, finalOffset, dynamicDuration);
          return;
        }
      }
    }

    // 降级：直接对齐最近选项
    console.log('[Touch] → snap (fallback)', { recentPointsCount: recentPoints.length });
    const index = getIndexByOffset(offset.value);
    scrollToIndex(index);
    onScrollTo(index);
  };

  /** 立即停止惯性动画并对齐到当前最近选项（由父组件 confirm 时调用） */
  const stopMomentum = () => {
    cancelAnimation();
    moving.value = false;
    const index = getIndexByOffset(offset.value);
    scrollToIndex(index, true);
    onScrollTo(index);
  };

  return {
    offset,
    duration,
    moving,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    scrollToIndex,
    stopMomentum,
    getIndexByOffset,
    getBaseOffset,
  };
}
